import { useState, useEffect, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Shield, HardDrive, Cloud, CloudOff, Download, RotateCcw,
  Loader2, X, Check, AlertTriangle, RefreshCw, FileText
} from "lucide-react";

export function BackupManagerModal({ open, onClose }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [remoteAvailable, setRemoteAvailable] = useState(false);

  const loadBackups = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/admin/backups`);
      if (resp.ok) {
        const data = await resp.json();
        setBackups(data.backups || []);
        setRemoteAvailable(data.remote_available || false);
      }
    } catch (e) {
      toast.error("Erreur chargement sauvegardes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadBackups();
  }, [open, loadBackups]);

  const handleBackupNow = async () => {
    setBackingUp(true);
    try {
      const resp = await authFetch(`${API}/admin/backup`, { method: "POST" });
      if (resp.ok) {
        const data = await resp.json();
        toast.success(data.message);
        loadBackups();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de sauvegarde");
        console.error("Backup error:", err);
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    } finally {
      setBackingUp(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const resp = await authFetch(`${API}/admin/backup/${filename}/download`);
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error("Erreur de téléchargement");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
  };

  const handleRestore = async (filename) => {
    setRestoring(filename);
    try {
      const resp = await authFetch(`${API}/admin/backup/restore/${filename}`, { method: "POST" });
      if (resp.ok) {
        const data = await resp.json();
        const totalDocs = Object.values(data.collections_restored)
          .filter(v => typeof v === "number")
          .reduce((a, b) => a + b, 0);
        toast.success(`Restauration terminée (${totalDocs} documents depuis ${data.source})`);
        setConfirmRestore(null);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de restauration");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    } finally {
      setRestoring(null);
    }
  };

  const handleExcelExport = async () => {
    try {
      toast.info("Export Excel en cours...");
      const resp = await authFetch(`${API}/admin/backup/export-excel`, { method: "POST" });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `toileorder_backup_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Export Excel téléchargé");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return dateStr; }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="backup-manager-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold">Gestion des sauvegardes</h2>
            {remoteAvailable ? (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <Cloud className="w-3 h-3" /> Stockage distant actif
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                <CloudOff className="w-3 h-3" /> Local uniquement
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="backup-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Actions bar */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 border-b">
          <Button
            size="sm"
            onClick={handleBackupNow}
            disabled={backingUp}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="backup-trigger-btn"
          >
            {backingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
            {backingUp ? "Sauvegarde..." : "Nouvelle sauvegarde"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleExcelExport} data-testid="backup-excel-export-btn">
            <FileText className="w-4 h-4 mr-1" />
            Export Excel
          </Button>
          <Button size="sm" variant="ghost" onClick={loadBackups} disabled={loading} data-testid="backup-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Backup list */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading && backups.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucune sauvegarde disponible
            </div>
          ) : (
            <div className="space-y-2">
              {backups.map((b) => (
                <div
                  key={b.filename}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  data-testid={`backup-item-${b.filename}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium truncate">{formatDate(b.date)}</span>
                      <span className="text-xs text-gray-500">{b.size}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {b.local && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Local</span>
                      )}
                      {b.remote && (
                        <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Cloud className="w-3 h-3" /> Distant
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(b.filename)}
                      title="Télécharger"
                      data-testid={`backup-download-${b.filename}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {confirmRestore === b.filename ? (
                      <div className="flex items-center gap-1 bg-red-50 rounded-lg px-2 py-1">
                        <span className="text-xs text-red-700 font-medium">Confirmer ?</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-100"
                          onClick={() => handleRestore(b.filename)}
                          disabled={restoring === b.filename}
                          data-testid={`backup-restore-confirm-${b.filename}`}
                        >
                          {restoring === b.filename ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => setConfirmRestore(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmRestore(b.filename)}
                        title="Restaurer"
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        data-testid={`backup-restore-${b.filename}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            La restauration remplace toutes les données actuelles
          </div>
          <span>{backups.length} sauvegarde(s)</span>
        </div>
      </div>
    </div>
  );
}
